import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  AlertCircle,
  Hash,
  CreditCard,
  Clock,
  RotateCcw,
  Headphones,
  ShoppingBag,
  ArrowRight
} from 'lucide-react';

const PaymentFailure = () => {
  const location = useLocation();

  const orderData = location.state?.order || {
    order_number: 'ORD-173026',
    total_amount: 108558,
    created_at: new Date().toISOString()
  };

  const reason = location.state?.reason || 'Your payment was declined by the bank. (Error Code: 001)';

  const orderDateFormatted = new Date(orderData.created_at || Date.now()).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '60px 24px 80px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '680px', width: '100%', margin: '0 auto', textAlign: 'center' }}>
        
        {/* RED CIRCLE WITH WHITE X MARK */}
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '24px' }}>
          <div style={{
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            right: '-20px',
            bottom: '-20px',
            background: 'radial-gradient(circle, rgba(220, 38, 38, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }} />

          <div style={{
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            background: '#dc2626',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            boxShadow: '0 12px 30px rgba(220, 38, 38, 0.35)',
            position: 'relative',
            zIndex: 2
          }}>
            <X size={56} strokeWidth={3} />
          </div>
        </div>

        {/* HEADLINE & SUBHEAD */}
        <h1 style={{ fontSize: '36px', fontWeight: 900, color: '#0f172a', margin: '0 0 10px 0', letterSpacing: '-0.02em' }}>
          Payment Failed
        </h1>
        <p style={{ fontSize: '15px', color: '#64748b', margin: '0 0 36px 0' }}>
          We couldn't process your payment. Please try again or use a different payment method.
        </p>

        {/* FAILURE REASON & ORDER SUMMARY CARD MATCHING FAILURE.PNG 1:1 */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', marginBottom: '44px', textAlign: 'left', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
          
          {/* Light Red Top Highlight Box */}
          <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '16px', padding: '16px 20px', marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 900, color: '#dc2626', textTransform: 'uppercase', marginBottom: '4px' }}>Reason</div>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#991b1b', lineHeight: 1.4 }}>
              {reason}
            </div>
          </div>

          {/* Details List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13.5px', padding: '0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontWeight: 700 }}>
                <Hash size={18} color="#dc2626" />
                <span>Order Number</span>
              </div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{orderData.order_number}</div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontWeight: 700 }}>
                <CreditCard size={18} color="#dc2626" />
                <span>Amount</span>
              </div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>
                £{parseFloat(orderData.total_amount || 0).toFixed(2)}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontWeight: 700 }}>
                <Clock size={18} color="#dc2626" />
                <span>Date & Time</span>
              </div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{orderDateFormatted}</div>
            </div>
          </div>

        </div>

        {/* WHAT CAN YOU DO? HELP CARDS MATCHING FAILURE.PNG 1:1 */}
        <div style={{ marginBottom: '44px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', margin: '0 0 24px 0' }}>What can you do?</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            
            {/* Option 1 */}
            <div style={{ background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                <RotateCcw size={18} />
              </div>
              <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', marginBottom: '4px' }}>Try Again</div>
              <div style={{ fontSize: '11.5px', color: '#64748b', lineHeight: 1.4 }}>Use the same payment method and try once more.</div>
            </div>

            {/* Option 2 */}
            <div style={{ background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                <CreditCard size={18} />
              </div>
              <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', marginBottom: '4px' }}>Change Payment Method</div>
              <div style={{ fontSize: '11.5px', color: '#64748b', lineHeight: 1.4 }}>Choose a different payment method to complete payment.</div>
            </div>

            {/* Option 3 */}
            <div style={{ background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                <Headphones size={18} />
              </div>
              <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', marginBottom: '4px' }}>Contact Support</div>
              <div style={{ fontSize: '11.5px', color: '#64748b', lineHeight: 1.4 }}>Our team is here to help you resolve the issue.</div>
            </div>

          </div>
        </div>

        {/* SINGLE ORDER AGAIN BUTTON DIRECTING TO PRODUCTS / SHOP PAGE */}
        <div style={{ maxWidth: '420px', margin: '0 auto' }}>
          <Link
            to="/shop"
            style={{
              width: '100%',
              background: '#dc2626',
              color: '#ffffff',
              borderRadius: '12px',
              padding: '16px 24px',
              fontSize: '15px',
              fontWeight: 900,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 8px 20px rgba(220,38,38,0.25)',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box'
            }}
          >
            <ShoppingBag size={18} />
            <span>Order Again</span>
            <ArrowRight size={18} />
          </Link>
        </div>

      </div>
    </div>
  );
};

export default PaymentFailure;
